import { useCallback, useState } from 'react';

interface UndoRedoState<T> {
  state: T;
  setState: (newState: T | ((prev: T) => T)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (initialState: T) => void;
}

/**
 * Custom hook for undo/redo functionality
 * Stores history of state changes and allows navigation
 */
export function useUndoRedo<T>(initialState: T, maxHistory: number = 50): UndoRedoState<T> {
  const [historyState, setHistoryState] = useState<{
    history: T[];
    index: number;
  }>({
    history: [initialState],
    index: 0,
  });

  const { history, index } = historyState;
  const state = history[index];

  const setState = useCallback((newState: T | ((prev: T) => T)) => {
    setHistoryState((prev) => {
      const { history, index } = prev;
      const currentState = history[index];
      const resolved = typeof newState === 'function'
        ? (newState as (prev: T) => T)(currentState)
        : newState;

      // If state hasn't changed, don't add to history
      if (JSON.stringify(resolved) === JSON.stringify(currentState)) {
        return prev;
      }

      // Remove future states and add new state
      const newHistory = history.slice(0, index + 1);
      newHistory.push(resolved);

      // Limit history size
      if (newHistory.length > maxHistory) {
        newHistory.shift();
        return {
          history: newHistory,
          index: newHistory.length - 1,
        };
      }

      return {
        history: newHistory,
        index: newHistory.length - 1,
      };
    });
  }, [maxHistory]);

  const undo = useCallback(() => {
    setHistoryState((prev) => ({
      ...prev,
      index: Math.max(0, prev.index - 1),
    }));
  }, []);

  const redo = useCallback(() => {
    setHistoryState((prev) => ({
      ...prev,
      index: Math.min(prev.history.length - 1, prev.index + 1),
    }));
  }, []);

  const reset = useCallback((newInitialState: T) => {
    setHistoryState({
      history: [newInitialState],
      index: 0,
    });
  }, []);

  const canUndo = index > 0;
  const canRedo = index < history.length - 1;

  return { state, setState, undo, redo, canUndo, canRedo, reset };
}

export default useUndoRedo;
